/**
 * Sign-in hand-offs that arrive in the URL, as the app shell reads them.
 *
 * index.html's inline script runs before any other script on the page. It
 * moves each secret out of the address bar into sessionStorage, and when that
 * fails it still scrubs the URL, drops the callback, and records what failed
 * in `window.__babaAuthHandoffFailed` for the shell to explain.
 */
import { readJwtClaims } from './baba-auth'

/** A Google session hand-off, read out of the JWT it carries. */
export interface PendingSession {
  session: string
  /** From the token's own claims, never from the link's `email=`: the link is
   *  anyone's to write, so it is not what the visitor is told they sign in as. */
  email: string
  isNewUser: boolean
  isEmailChange: boolean
  /** `reg_<userId>` from the fragment's `event_id`, for a new Google sign-up. */
  eventId?: string
}

export type SessionHandoff = PendingSession | 'invalid' | 'expired' | null

/**
 * The Google hand-off parameters (`session`, `email`, `new`, `email_changed`)
 * as something the shell can put in front of the visitor. Never adopted from
 * here: every one goes through "Continue as <email>?" (see lib/googleSignIn.ts
 * for why, and for the nonce follow-up that would lift it).
 */
export function readSessionHandoff(params: URLSearchParams | null, now: number = Date.now()): SessionHandoff {
  const session = params?.get('session')
  if (!params || !session) return null
  const claims = readJwtClaims(session)
  const email = typeof claims?.email === 'string' ? claims.email.trim() : ''
  if (!email) return 'invalid'
  if (typeof claims?.exp === 'number' && claims.exp * 1000 <= now) return 'expired'
  const eventId = params.get('event_id')
  return { session, email, isNewUser: params.get('new') === '1', isEmailChange: params.get('email_changed') === '1', ...(eventId ? { eventId } : {}) }
}

export type HandoffFailure = 'session' | 'link' | 'claim'

/** What the visitor reads when index.html could not hand a URL secret over. */
export const HANDOFF_FAILED_MESSAGES: Record<HandoffFailure, string> = {
  session: 'Google sign-in could not finish in this browser. Try again, or sign in with an emailed code.',
  link: 'That sign-in link could not be used here. Sign in with the 6-digit code from the same email instead.',
  claim: 'That email claim link could not be opened in this browser. Open it again, or add the address in email settings.',
}

/** The failures index.html recorded, removed as they are read. */
export function takeHandoffFailures(win: { __babaAuthHandoffFailed?: unknown }): HandoffFailure[] {
  const recorded = win.__babaAuthHandoffFailed
  delete win.__babaAuthHandoffFailed
  if (!Array.isArray(recorded)) return []
  return recorded.filter((kind): kind is HandoffFailure => kind === 'session' || kind === 'link' || kind === 'claim')
}
